import { useEffect, useState, type FC } from "react";

import { IconDot } from "@/assets/icons/geist/IconDot";
import type { Size } from "@/assets/icons/utils";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";

export type StatusDotAnimation = "none" | "ping" | "heartbeat" | "fade" | "spin" | "spin-reverse" | "pulse";
export type StatusDotVariant = "solid" | "small" | "ring";
export type StatusDotColor = "green" | "brand" | "orange" | "red" | "gray";

export type StatusDotProps = {
    size: Size;
    color: StatusDotColor;
    variant?: StatusDotVariant;
    animation?: StatusDotAnimation;
    tooltip?: string;
} & PropsWithClassName;

const colorToClass: Record<StatusDotColor, string> = {
    green: "text-content-green",
    brand: "text-content-brand",
    orange: "text-content-orange",
    red: "text-content-red",
    gray: "text-content-tertiary",
};

export const StatusDot: FC<StatusDotProps> = ({
    size,
    color,
    variant = "solid",
    animation = "none",
    tooltip,
    className,
}) => {
    const colorClass = colorToClass[color];

    const content = (
        <StatusDotContent
            size={size}
            colorClass={colorClass}
            variant={variant}
            animation={animation}
            tooltip={tooltip}
            className={className}
        />
    );

    if (tooltip) {
        return (
            <Tooltip content={tooltip} usePortal>
                <span className="inline-flex">{content}</span>
            </Tooltip>
        );
    }

    return content;
};

type StatusDotContentProps = {
    size: Size;
    colorClass: string;
    variant: StatusDotVariant;
    animation: StatusDotAnimation;
    tooltip?: string;
    className?: string;
};

const svgBase = { position: "absolute" as const, inset: 0 };

const StatusDotContent: FC<StatusDotContentProps> = ({ size, colorClass, variant, animation, tooltip, className }) => {
    // For animated states with lg size, render custom SVG
    if (animation !== "none" && size === "lg") {
        if (animation === "spin") {
            return (
                <div
                    aria-label={tooltip}
                    data-testid="status-dot"
                    className={cn("relative block", colorClass, className)}
                    style={{ width: 24, height: 24 }}
                >
                    <style>
                        {`
                            @keyframes spin-ring {
                                0% {
                                    transform: rotate(0deg);
                                }
                                100% {
                                    transform: rotate(360deg);
                                }
                            }
                            .spin-ring {
                                transform-origin: center;
                                animation: spin-ring 2s linear infinite;
                                will-change: transform;
                            }
                        `}
                    </style>
                    {/* Static inner dot */}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={svgBase}>
                        <circle
                            cx="12"
                            cy="12"
                            r="3.5"
                            fill="currentColor"
                            stroke="currentColor"
                            strokeOpacity="0.15"
                            strokeWidth="6"
                        />
                    </svg>
                    {/* Spinning arc — animation on the SVG element itself, fully compositor-driven */}
                    <svg className="spin-ring" width="24" height="24" viewBox="0 0 24 24" fill="none" style={svgBase}>
                        <circle
                            cx="12"
                            cy="12"
                            r="6"
                            fill="none"
                            stroke="currentColor"
                            strokeOpacity="0.5"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeDasharray="7 31"
                        />
                    </svg>
                </div>
            );
        }

        if (animation === "spin-reverse") {
            return (
                <div
                    aria-label={tooltip}
                    data-testid="status-dot"
                    className={cn("relative block", colorClass, className)}
                    style={{ width: 24, height: 24 }}
                >
                    <style>
                        {`
                            @keyframes spin-ring-reverse {
                                0% {
                                    transform: rotate(360deg);
                                }
                                100% {
                                    transform: rotate(0deg);
                                }
                            }
                            .spin-ring-reverse {
                                transform-origin: center;
                                animation: spin-ring-reverse 2s linear infinite;
                                will-change: transform;
                            }
                        `}
                    </style>
                    {/* Static inner dot */}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={svgBase}>
                        <circle
                            cx="12"
                            cy="12"
                            r="3.5"
                            fill="currentColor"
                            stroke="currentColor"
                            strokeOpacity="0.15"
                            strokeWidth="6"
                        />
                    </svg>
                    {/* Reverse spinning arc — animation on the SVG element itself, fully compositor-driven */}
                    <svg
                        className="spin-ring-reverse"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        style={svgBase}
                    >
                        <circle
                            cx="12"
                            cy="12"
                            r="6"
                            fill="none"
                            stroke="currentColor"
                            strokeOpacity="0.5"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeDasharray="7 31"
                        />
                    </svg>
                </div>
            );
        }

        if (animation === "fade") {
            return (
                <div
                    aria-label={tooltip}
                    data-testid="status-dot"
                    className={cn("relative block", colorClass, className)}
                    style={{ width: 24, height: 24 }}
                >
                    <style>
                        {`
                            @keyframes fade-pulse {
                                0%, 100% {
                                    opacity: 0.9;
                                }
                                50% {
                                    opacity: 0.5;
                                }
                            }
                            .fade-dot {
                                animation: fade-pulse 2.5s ease-in-out infinite;
                                will-change: opacity;
                            }
                        `}
                    </style>
                    <svg className="fade-dot" width="24" height="24" viewBox="0 0 24 24" fill="none" style={svgBase}>
                        <circle cx="12" cy="12" r="4" fill="currentColor" />
                    </svg>
                </div>
            );
        }

        if (animation === "heartbeat") {
            return (
                <div
                    aria-label={tooltip}
                    data-testid="status-dot"
                    className={cn("relative block", colorClass, className)}
                    style={{ width: 24, height: 24 }}
                >
                    <style>
                        {`
                            @keyframes hb-pulse {
                                0% {
                                    transform: scale(1);
                                    opacity: 0.4;
                                }
                                100% {
                                    transform: scale(2.5);
                                    opacity: 0;
                                }
                            }
                            .hb-1 {
                                animation: hb-pulse 0.5s ease-out infinite;
                                animation-duration: 3s;
                                will-change: transform, opacity;
                                transform-origin: center;
                            }
                            .hb-2 {
                                animation: hb-pulse 0.5s ease-out infinite;
                                animation-delay: 1.2s;
                                animation-duration: 3s;
                                will-change: transform, opacity;
                                transform-origin: center;
                            }
                            .hb-3 {
                                animation: hb-pulse 0.5s ease-out infinite;
                                animation-delay: 1.85s;
                                animation-duration: 3s;
                                will-change: transform, opacity;
                                transform-origin: center;
                            }
                        `}
                    </style>
                    <svg className="hb-1" width="24" height="24" viewBox="0 0 24 24" fill="none" style={svgBase}>
                        <circle cx="12" cy="12" r="4" fill="currentColor" />
                    </svg>
                    <svg className="hb-2" width="24" height="24" viewBox="0 0 24 24" fill="none" style={svgBase}>
                        <circle cx="12" cy="12" r="4" fill="currentColor" />
                    </svg>
                    <svg className="hb-3" width="24" height="24" viewBox="0 0 24 24" fill="none" style={svgBase}>
                        <circle cx="12" cy="12" r="4" fill="currentColor" />
                    </svg>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={svgBase}>
                        <circle cx="12" cy="12" r="4" fill="currentColor" />
                    </svg>
                </div>
            );
        }

        if (animation === "pulse") {
            return <PulseDot colorClass={colorClass} tooltip={tooltip} className={className} />;
        }

        // ping animation (default for animated)
        return (
            <div
                aria-label={tooltip}
                data-testid="status-dot"
                className={cn("relative block", colorClass, className)}
                style={{ width: 24, height: 24 }}
            >
                <style>
                    {`
                        @keyframes raindrop {
                            0% {
                                transform: scale(1);
                                opacity: 0.4;
                            }
                            100% {
                                transform: scale(2.5);
                                opacity: 0;
                            }
                        }
                        .raindrop {
                            animation: raindrop 1.5s ease-out infinite;
                            will-change: transform, opacity;
                            transform-origin: center;
                        }
                    `}
                </style>
                <svg className="raindrop" width="24" height="24" viewBox="0 0 24 24" fill="none" style={svgBase}>
                    <circle cx="12" cy="12" r="4" fill="currentColor" />
                </svg>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={svgBase}>
                    <circle cx="12" cy="12" r="4" fill="currentColor" />
                </svg>
            </div>
        );
    }

    // Solid dot without outer ring
    if (variant === "solid" && size === "lg") {
        return (
            <svg
                aria-label={tooltip}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={cn("block", colorClass, className)}
                data-testid="status-dot"
            >
                <circle cx="12" cy="12" r="4" fill="currentColor" />
            </svg>
        );
    }

    // Small dot for inactive/stopped states
    if (variant === "small" && size === "lg") {
        return (
            <svg
                aria-label={tooltip}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={cn("block", colorClass, className)}
                data-testid="status-dot"
            >
                <circle cx="12" cy="12" r="2" fill="currentColor" />
            </svg>
        );
    }

    // Default: use IconDot for ring variant or non-lg sizes.
    // Map animation prop to a Tailwind animation class so callers don't need to
    // work around the lg-only SVG animations with manual className overrides.
    const animationClass = animation !== "none" ? "animate-pulse" : undefined;
    return (
        <IconDot
            aria-label={tooltip}
            size={size}
            className={cn("block", colorClass, animationClass, className)}
            data-testid="status-dot"
        />
    );
};

/**
 * 3 expanding rings that play once on mount then disappear,
 * leaving a steady solid dot. The parent must use `visibility: hidden`
 * (not `display: none`) to hide this component, otherwise the browser
 * restarts the CSS animation when the element becomes visible again.
 */
const PulseDot: FC<{ colorClass: string; tooltip?: string; className?: string }> = ({
    colorClass,
    tooltip,
    className,
}) => {
    const [showRings, setShowRings] = useState(true);

    useEffect(() => {
        // Last ring starts at 1.6s, animation lasts 0.6s → done at 2.2s
        const timer = setTimeout(() => setShowRings(false), 2400);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div
            aria-label={tooltip}
            data-testid="status-dot"
            className={cn("relative block", colorClass, className)}
            style={{ width: 24, height: 24 }}
        >
            {showRings && (
                <>
                    <style>
                        {`
                            @keyframes pulse-ring {
                                0% {
                                    transform: scale(1);
                                    opacity: 0.4;
                                }
                                100% {
                                    transform: scale(2.5);
                                    opacity: 0;
                                }
                            }
                            .pulse-ring-svg {
                                transform-origin: center;
                                will-change: transform, opacity;
                                animation: pulse-ring 0.6s ease-out forwards;
                                opacity: 0;
                            }
                            .pulse-ring-svg-1 { animation-delay: 0s; }
                            .pulse-ring-svg-2 { animation-delay: 0.8s; }
                            .pulse-ring-svg-3 { animation-delay: 1.6s; }
                        `}
                    </style>
                    <svg
                        className="pulse-ring-svg pulse-ring-svg-1"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        style={svgBase}
                    >
                        <circle cx="12" cy="12" r="4" fill="currentColor" />
                    </svg>
                    <svg
                        className="pulse-ring-svg pulse-ring-svg-2"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        style={svgBase}
                    >
                        <circle cx="12" cy="12" r="4" fill="currentColor" />
                    </svg>
                    <svg
                        className="pulse-ring-svg pulse-ring-svg-3"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        style={svgBase}
                    >
                        <circle cx="12" cy="12" r="4" fill="currentColor" />
                    </svg>
                </>
            )}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={svgBase}>
                <circle cx="12" cy="12" r="4" fill="currentColor" />
            </svg>
        </div>
    );
};
